/**
 * Epic-Ready Operative Note Generator
 * 
 * Formats surgeon dictation/transcript into a clean, professional operative note
 * matching common Epic standards. Content is derived ONLY from surgeon dictation
 * and confirmations — no AI-authored language.
 * 
 * HARD BOUNDARIES:
 * - Content comes exclusively from surgeon dictation/transcript
 * - No clinical content is invented or added
 * - No claim submission occurs
 */

export interface OperativeNoteInput {
  /** Case metadata */
  caseId: string;
  caseNumber: string;
  dateOfService?: string;
  laterality?: string;
  
  /** Surgeon info */
  surgeonName?: string;
  assistantSurgeonName?: string;
  attestationTimestamp?: string;
  
  /** Structured note sections (from dictation or op note) */
  preoperativeDiagnosis?: string;
  postoperativeDiagnosis?: string;
  proceduresPerformed?: string;
  anesthesia?: string;
  indications?: string;
  findings?: string;
  procedureDescription?: string;
  implants?: string;
  estimatedBloodLoss?: string;
  complications?: string;
  specimens?: string;
  disposition?: string;
  closure?: string;
  
  /** Full raw transcript as fallback */
  rawTranscript?: string;
  
  /** Demo mode */
  isDemoExport: boolean;
}

/**
 * Generate a clean, professional operative note formatted for Epic copy/paste.
 * Content is organized into standard Epic sections in the required order.
 */
export function generateEpicOperativeNote(input: OperativeNoteInput): string {
  const lines: string[] = [];

  // Demo watermark
  if (input.isDemoExport) {
    lines.push('*** DEMO MODE — ILLUSTRATIVE ONLY — NO CLAIMS SUBMITTED ***');
    lines.push('');
  }

  // Header block — matches Dr. Larkin's note format
  lines.push(`Patient: ${input.caseNumber}`);
  if (input.dateOfService) {
    lines.push(`Date of Birth: `);
    lines.push(`Date of Service: ${input.dateOfService}`);
  }
  lines.push('');
  lines.push(`Location: `);
  lines.push(`Visit Type: Operative note`);
  if (input.surgeonName) {
    lines.push(`Provider: ${input.surgeonName}`);
  }
  lines.push('');

  lines.push(`Purpose: Operative Note`);
  lines.push('');

  // Preoperative Diagnosis
  addSectionInline(lines, 'Preoperative Diagnosis', input.preoperativeDiagnosis || input.indications);

  // Postoperative Diagnosis
  if (input.postoperativeDiagnosis && input.postoperativeDiagnosis === input.preoperativeDiagnosis) {
    lines.push('Postoperative Diagnosis: Same.');
    lines.push('');
  } else {
    addSectionInline(lines, 'Postoperative Diagnosis', input.postoperativeDiagnosis || input.indications);
  }

  // Procedures Performed
  addSectionInline(lines, 'Procedures Performed', input.proceduresPerformed);

  // Surgeon
  if (input.surgeonName) {
    lines.push(`Surgeon: ${input.surgeonName}`);
    lines.push('');
  }

  // Assistant Surgeon
  if (input.assistantSurgeonName) {
    lines.push(`Assistant Surgeon: ${input.assistantSurgeonName}`);
    lines.push('');
  }

  // History (indications as narrative)
  addSectionHeader(lines, 'History', input.indications);

  // Operative Procedure (main narrative body)
  const procedureBody = [
    input.procedureDescription || input.rawTranscript,
    input.findings ? `\n${input.findings}` : '',
    input.implants ? `\nImplants utilized: ${input.implants}.` : '',
    input.closure ? `\n${input.closure}` : '',
    input.estimatedBloodLoss ? `\nEstimated blood loss was ${input.estimatedBloodLoss}.` : '',
    input.complications ? `\nComplications: ${input.complications}.` : '\nComplications: None.',
    input.specimens ? `\nSpecimens: ${input.specimens}.` : '',
  ].filter(Boolean).join('');

  addSectionHeader(lines, 'Operative Procedure', procedureBody);

  // Disposition as closing paragraph
  if (input.disposition) {
    lines.push(input.disposition);
    lines.push('');
  }

  // Signature block
  if (input.surgeonName) {
    lines.push(input.surgeonName);
    lines.push('');
  }

  if (input.isDemoExport) {
    lines.push('*** DEMO DATA — NOT FOR CLINICAL OR BILLING USE ***');
  }

  return lines.join('\n');
}

/** "Label: content" on same line or "Label:\ncontent" for multi-line */
function addSectionInline(lines: string[], label: string, content?: string): void {
  if (!content?.trim()) return;
  const trimmed = content.trim();
  if (trimmed.includes('\n')) {
    lines.push(`${label}:`);
    lines.push(trimmed);
  } else {
    lines.push(`${label}: ${trimmed}`);
  }
  lines.push('');
}

/** Bold-style header on its own line, content as paragraph below */
function addSectionHeader(lines: string[], title: string, content?: string): void {
  if (!content?.trim()) return;
  lines.push(title);
  lines.push(content.trim());
  lines.push('');
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}
